// src/components/TagPicker.tsx
// Tag picker with search, colored badges, and create-on-the-fly.
// Uses TagService API via generated store.

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { observer } from '@react';
import { useTag, useI18n } from '@react/hooks';
import { tagApi } from '@core/api';
import type { Tag } from '@core/types/tag';
import { AnchoredMenu } from './AnchoredMenu';

interface TagPickerProps {
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  className?: string;
}

const TAG_COLORS = [
  '#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6',
  '#8b5cf6', '#ef4444', '#14b8a6', '#f97316', '#06b6d4',
];

function getDefaultColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

export const TagPicker = observer(({
  value: rawValue,
  onChange,
  label,
  placeholder,
  error,
  className = '',
}: TagPickerProps) => {
  const value = Array.isArray(rawValue) ? rawValue : [];
  const tagStore = useTag();
  const { t } = useI18n();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Tag[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Load initial tags on first open
  useEffect(() => {
    if (isOpen && tagStore.items.length === 0 && !tagStore.isLoading) {
      tagStore.ListTags({ pagination: { limit: 50, cursor: '', direction: 'forward' }, sort: [] });
    }
  }, [isOpen]);

  // Focus search on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Outside click, Escape and repositioning live in AnchoredMenu.

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setIsOpen(false);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!search.trim()) {
      setSearchResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await tagApi.SearchTags({ q: search.trim(), limit: 20 });
        setSearchResults(result.tags?.tags ?? []);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // Available tags: either search results or initial list, excluding already selected
  const availableTags = useMemo(() => {
    const source = search.trim() ? searchResults : (tagStore.items || []);
    const selectedIds = new Set(value.map(t => t.entity?.id));
    return source.filter(t => !selectedIds.has(t.entity?.id));
  }, [search, searchResults, tagStore.items, value]);

  // Can create new tag if search text doesn't match any existing tag name
  const canCreate = useMemo(() => {
    if (!search.trim()) return false;
    const q = search.trim().toLowerCase();
    const allTags = search.trim() ? searchResults : (tagStore.items || []);
    return !allTags.some(t => t.name.toLowerCase() === q) &&
      !value.some(t => t.name.toLowerCase() === q);
  }, [search, searchResults, tagStore.items, value]);

  const handleSelect = (tag: Tag) => {
    onChange([...value, tag]);
    setSearch('');
  };

  const handleRemove = (tagId: string) => {
    onChange(value.filter(t => t.entity?.id !== tagId));
  };

  const handleCreate = async () => {
    if (!search.trim() || isCreating) return;
    setIsCreating(true);
    try {
      const newTag = await tagStore.CreateTag({
        name: search.trim(),
        color: getDefaultColor(search.trim()),
      });
      if (newTag) {
        onChange([...value, newTag]);
        setSearch('');
      }
    } catch {
      // Error in store
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={containerRef} onKeyDown={handleKeyDown}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1.5">
          {label}
        </label>
      )}

      {/* Trigger area — shows selected tags + input */}
      <div
        ref={triggerRef}
        onClick={() => setIsOpen(true)}
        className={`
          w-full flex flex-wrap items-center gap-1.5 rounded-md border px-2.5 py-1.5 min-h-[38px] cursor-text
          ${error
            ? 'border-destructive focus-within:ring-destructive'
            : 'border-input focus-within:ring-ring'
          }
          ${isOpen ? 'ring-2 ring-ring' : ''}
          bg-background text-foreground
        `}
      >
        {value.map(tag => (
          <span
            key={tag.entity?.id}
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-white"
            style={{ backgroundColor: tag.color || '#6366f1' }}
          >
            {tag.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(tag.entity?.id || '');
              }}
              className="hover:opacity-75 ml-0.5"
              aria-label={t('common.remove')}
            >
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        {value.length === 0 && !isOpen && (
          <span className="text-sm text-muted-foreground">
            {placeholder || t('tag.selectTags')}
          </span>
        )}
        {isOpen && (
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canCreate) {
                e.preventDefault();
                handleCreate();
              }
              if (e.key === 'Backspace' && !search && value.length > 0) {
                handleRemove(value[value.length - 1].entity?.id || '');
              }
            }}
            className="flex-1 min-w-[80px] bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            placeholder={value.length > 0 ? t('tag.addMore') : (placeholder || t('tag.selectTags'))}
          />
        )}
      </div>

      {error && (
        <p className="mt-1 text-xs text-destructive">{error}</p>
      )}

      {/* Dropdown via portal */}
      <AnchoredMenu
        anchor={triggerRef}
        ignoreRef={containerRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        role="none"
        matchTriggerWidth
        maxHeight={280}
        className="overflow-hidden"
      >
        <div className="overflow-y-auto py-1" style={{ maxHeight: 'inherit' }}>
          {(tagStore.isLoading || isSearching) ? (
            <div className="px-3 py-4 text-center">
              <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p className="text-xs text-muted-foreground mt-1">{t('common.loading')}</p>
            </div>
          ) : availableTags.length === 0 && !canCreate ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-muted-foreground">
                {search ? t('common.noResults') : t('tag.noTags')}
              </p>
            </div>
          ) : (
            <>
              {availableTags.map((tag) => (
                <button
                  key={tag.entity?.id}
                  type="button"
                  onClick={() => handleSelect(tag)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted transition-colors"
                >
                  <span
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tag.color || '#6366f1' }}
                  />
                  <span className="text-sm text-foreground">{tag.name}</span>
                  {tag.usage_count > 0 && (
                    <span className="text-xs text-muted-foreground ml-auto">{tag.usage_count}</span>
                  )}
                </button>
              ))}

              {/* Create new tag option */}
              {canCreate && (
                <>
                  {availableTags.length > 0 && <div className="border-t border-border my-1" />}
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={isCreating}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted transition-colors"
                  >
                    <svg className="h-4 w-4 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    <span className="text-sm text-foreground">
                      {t('tag.createNew', { name: search.trim() })}
                    </span>
                    {isCreating && (
                      <div className="ml-auto inline-block h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </AnchoredMenu>
    </div>
  );
});

export default TagPicker;
